import { useHistory } from "react-router-dom";
import { Col, Dropdown, message, Row } from "antd";
import { useRef, useState } from "react";

import Table from "@components/Table";
import Button from "@components/Button";
import { getColumns } from "./config";
import {
  ErrorServices,
  useDeleteShortTermPlanMutation,
  useFetchShortTermPlansQuery,
} from "@services";
import { IShortTermPlan } from "@types";
import { paths, permissions } from "@router";
import AddShortTermPlan from "./AddShortTermPlan";
import { useTypedSelector } from "@hooks";
import { checkPermission } from "@/utils";
import SearchBox from "@/components/searchInput";
import CustomButton from "@/components/customButton";
import { MdOutlineFileDownload, MdOutlineFileUpload } from "react-icons/md";
import { DownOutlined } from "@ant-design/icons";
import useDebounce from "@/hooks/useDebounce";

const ShortTermPlans = () => {
  const { company } = useTypedSelector(state => state.auth?.user!);
  const { id: company_id } = company!;
  const history = useHistory();
  const shortTermPlan_id = useRef<any>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [isVisible, setIsVisible] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 500);
  const [isVisibleForEdit, setIsVisibleForEdit] = useState(false);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [order, setOrder] = useState<"asc" | "desc" | null>(null);

  const [selectedShortTermPlan, setSelectedShortTermPlan] =
    useState<IShortTermPlan | null>(null);
  const [deleteShortTermPlan, { isLoading: isDeleting }] =
    useDeleteShortTermPlanMutation();
  const { data: plansData, isLoading } = useFetchShortTermPlansQuery({
    company_id,
    page,
    page_size: pageSize,
    sort_by: sortBy,
    order,
    search: debouncedSearch,
  });
  const { data, pagination } = plansData || {};

  const onRowClick = (data: any) => {
    // history.push(`${window.location.pathname}/${data?.id}`);
    return;
  };

  const removeShortTermPlan = async (id: number) => {
    try {
      shortTermPlan_id.current = id;
      await deleteShortTermPlan({ company_id, id }).unwrap();
      message.success("Short term incentive plan deleted successfully!");
    } catch (error) {
      ErrorServices(error);
    }
  };

  const editShortTermPlan = (selectedShortTermPlan: IShortTermPlan) => {
    setSelectedShortTermPlan(selectedShortTermPlan);
    setIsVisibleForEdit(true);
  };

  const columns = getColumns({
    isDeleting,
    shortTermPlan_id,
    removeShortTermPlan,
    editShortTermPlan,
  });

  // ✅ COLUMN STATE
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(
    columns.map(col => col.key)
  );

  // ✅ FILTERED COLUMNS
  const filteredColumns = columns.filter(col =>
    visibleColumnKeys.includes(col.key)
  );

  // custom overlay
  const columnOverlay = (
    <div
      style={{
        background: "#fff",
        padding: 8,
        borderRadius: 6,
        boxShadow: "0 3px 10px rgba(0,0,0,0.15)",
        minWidth: 240,
        maxHeight: 300,
        overflowY: "auto",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {columns.map((col) => {
        const isActionsColumn = col.key === "actions";
        const isChecked = visibleColumnKeys.includes(col.key);
        const isDisabled =
          isActionsColumn || (isChecked && visibleColumnKeys.length <= 3);

        return (
          <label
            key={col.key}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "6px 8px",
              borderRadius: 4,
              cursor: isDisabled ? "not-allowed" : "pointer",
              opacity: isDisabled ? 0.6 : 1,
            }}
          >
            <input
              type="checkbox"
              checked={isChecked}
              disabled={isDisabled}
              onChange={() => {
                if (isActionsColumn) return;

                if (isChecked) {
                  setVisibleColumnKeys((prev) =>
                    prev.filter((key) => key !== col.key)
                  );
                } else {
                  setVisibleColumnKeys((prev) => [...prev, col.key]);
                }
              }}
              style={{
                margin: 0,
                width: 16,
                height: 16,
                flexShrink: 0,
              }}
            />

            <span
              style={{
                flex: 1,
                fontSize: 14,
                lineHeight: "18px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {typeof col.title === "string"
                ? col.title.replace(/\b\w/g, c => c.toUpperCase())
                : col.title}
            </span>
          </label>
        );
      })}
    </div>
  );

  return (
    <>
      {isVisible && (
        <AddShortTermPlan
          selectedShortTermPlan={selectedShortTermPlan}
          setSelectedShortTermPlan={setSelectedShortTermPlan}
          isVisible={isVisible}
          setIsVisible={setIsVisible}
        />
      )}
      {isVisibleForEdit && (
        <AddShortTermPlan
          selectedShortTermPlan={selectedShortTermPlan}
          setIsVisible={() => {
            setIsVisibleForEdit(false);
            setSelectedShortTermPlan(null);
          }}
          setSelectedShortTermPlan={setSelectedShortTermPlan}
          isVisible={isVisibleForEdit}
          isEdit={true}
        />
      )}

      <div className="bg-themeCore ">
        <div className="jobCreationBar bg-white px-4 py-6 rounded-lg flex justify-between items-center ">
          <div className="title text-themeBlue  font-medium text-3xl">
            Short Term Incentive Plans
          </div>

          <div className="flex flex-row justify-between">
            <CustomButton
              onClick={() =>
                history.push(paths.client.short_term_ip.incentives)
              }
              btnName="STI Data"
              customClasses="shadow-md font-medium border py-1 px-6 !border-themeBorder"
              iconPosition="left"
            ></CustomButton>

            {checkPermission(permissions.CREATE_COMPANY_STI) && (
              <CustomButton
                onClick={() => setIsVisible(true)}
                btnName="Create STI Plan"
                customClasses="shadow-md font-medium border py-1 px-6 !border-themeBorder ml-4"
                iconPosition="right"
              >
                <MdOutlineFileDownload
                  size={20}
                  className="text-white font-bold"
                />
              </CustomButton>
            )}
          </div>
        </div>
      </div>

      <div className="jobCreationListing mt-10 bg-white px-14 py-6 rounded-lg">
        <div className="flex items-center justify-end gap-2">
          <SearchBox
            placeholder="Type to search..."
            value={searchInput}
            onChange={value => {
              setSearchInput(value);
              setPage(1);
            }}
          />
          <Dropdown
            overlay={columnOverlay}
            trigger={["click"]}
            placement="bottomLeft"
          // getPopupContainer={trigger => trigger.parentElement}
          >
            <CustomButton
              btnName="Columns"
              customClasses="shadow-md font-medium border py-1 px-4"
            >
              <DownOutlined style={{ marginLeft: 6 }} />
            </CustomButton>
          </Dropdown>
        </div>
        <Row>
          <Table
            scroll={1500}
            data={data}
            columns={filteredColumns}
            isLoading={isLoading}
            pagination={true}
            count={pagination?.count}
            onChangePage={setPage}
            page={page}
            onRowClick={onRowClick}
            onPageSizeChange={setPageSize}
            onTableChange={(pagination, filters, sorter) => {
              if (sorter?.field) {
                setSortBy(sorter.field);
                setOrder(sorter.order === "ascend" ? "asc" : "desc");
                setPage(1);
              }
            }}
          />
        </Row>
      </div>
    </>
  );
};

export default ShortTermPlans;
